import path from 'path';
import {
  AddPanelMap64Icon,
  AddPanelSocialFacebook64Icon,
  AddPanelSocialTwitter64Icon,
  ElementTwitterIcon as TwitterIcon,
  ElementMapIcon as MapIcon,
  ElementFacebookIcon as FacebookIcon,
} from '@packages/systems/spring';
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import Facebook from './Facebook';
import MapWidget, {mapWidgetFactory} from './MapWidget';
import Twitter from './Twitter';
import {Plugins, ElementPreset, Component} from '@packages/systems/wfdl';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import MapWidgetDataType from './datatypes/MapWidgetDataType';
import GooglePlus from './DEPRECATED_GooglePlus';

const FacebookAddPanelIcon = wrapAddPanelIcon(AddPanelSocialFacebook64Icon);
const MapAddPanelIcon = wrapAddPanelIcon(AddPanelMap64Icon);
const TwitterAddPanelIcon = wrapAddPanelIcon(AddPanelSocialTwitter64Icon);

import {
  EElement,
  EText,
  ERecord,
  EBoolean,
  ENumber,
  EEnum,
  EUnion,
} from '@packages/systems/wfdl-ast';

export const plugin = Plugins.PrivilegedPlugin({
  atoms: {
    Facebook,
    MapWidget,
    Twitter,
    // GooglePlus was deprecated in April of 2019. Keeping it here lets existing
    // elements to render. We could eventually create a DeprecatedElement Atom or
    // Plugin Component and write a migration to convert GooglePlus elements to it.
    GooglePlus,
  },
  icons: {
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelMap: MapAddPanelIcon,
    Map: MapIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelFacebook: FacebookAddPanelIcon,
    Facebook: FacebookIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelTwitter: TwitterAddPanelIcon,
    Twitter: TwitterIcon,
  },
  dataTypes: {
    MapWidget: MapWidgetDataType,
  },
  elementPresets: {
    MapWidget: ElementPreset.create({
      factory: mapWidgetFactory,
      icon: {
        small: qualify(Plugins.name('Widget'), iconName('Map')),
        medium: qualify(Plugins.name('Widget'), iconName('AddPanelMap')),
      },
      label: 'Map',
      helpData: {
        description: [
          'Add an interactive map to your site (shout out to Google Maps). Specify the highlighted location and choose from various display options.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/map.c759f85bc6.mp4',
      },
      category: 'Other',
      addTabIndex: 9,
    }),
    Facebook: ElementPreset.create({
      factory: EElement({
        id: 'Facebook',
        type: qualify(Plugins.name('Widget'), Component.name('Facebook')),
        data: ERecord({
          widget: ERecord({
            html: EBoolean(true),
            layout: EEnum('standard'),
            width: ENumber(225),
            height: ENumber(35),
            type: EText('facebook'),
            url: EText(''),
            locale: EEnum('en_US'),
          }),
          attr: ERecord({}),
          value: EText(
            '<iframe src="//www.facebook.com/plugins/like.php?href=https%3A%2F%2Ffacebook.com%2Fwebflow&amp;layout=standard&amp;locale=en_US&amp;action=like&amp;show_faces=false&amp;share=false" scrolling="no" frameborder="0" allowtransparency="true" style="border: none; overflow: hidden; width: 225px; height: 35px;"></iframe>'
          ),
        }),
      }),
      icon: {
        small: qualify(Plugins.name('Widget'), iconName('Facebook')),
        medium: qualify(Plugins.name('Widget'), iconName('AddPanelFacebook')),
      },
      label: 'Facebook',
      helpData: {
        description: [
          'Encourage visitors to like and share your Facebook page. Specify the URL and choose from 3 standard layouts.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/facebook.d9c3e1c618.mp4',
      },
      category: 'Other',
      addTabIndex: 11,
    }),
    Twitter: ElementPreset.create({
      factory: EElement({
        id: 'Twitter',
        type: qualify(Plugins.name('Widget'), Component.name('Twitter')),
        data: ERecord({
          widget: ERecord({
            mode: EUnion({
              tag: 'tweet',
              value: ERecord({
                url: EText(''),
                text: EText(''),
                show_count: EUnion({
                  tag: 'true',
                  value: ERecord({
                    __deprecated__count_layout: EEnum('horizontal'),
                  }),
                }),
              }),
            }),
            size: EEnum('m'),
            type: EText('twitter'),
            html: EBoolean(true),
          }),
          value: EText(
            '<iframe allowtransparency="true" frameborder="0" scrolling="no" src="//platform.twitter.com/widgets/tweet_button.html#counturl=webflow.com&amp;dnt=true&amp;height=21&amp;show_count=horizontal&amp;size=m&amp;text=Check%20out%20this%20site&amp;url=http%3A%2F%2Fwebflow.com&amp;width=62" style="border: none; height: 21px; overflow: hidden; width: 62px;"></iframe><div class="w-widget-twitter-count-shim"><div class="w-widget-twitter-count-inner">...</div></div>'
          ),
        }),
      }),
      icon: {
        small: qualify(Plugins.name('Widget'), iconName('Twitter')),
        medium: qualify(Plugins.name('Widget'), iconName('AddPanelTwitter')),
      },
      label: 'X (Twitter)',
      helpData: {
        description: [
          'Encourage visitors to Post about your site or follow you on X (Twitter). Specify the URL and choose from a couple standard layouts.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/twitter.e399c4784f.mp4',
      },
      category: 'Other',
      addTabIndex: 12,
    }),
  },

  siteModules: [
    {
      // TODO remove sync path usage: https://webflow.atlassian.net/browse/DEVX-3220
      filePaths: [path.resolve(__dirname, 'webflow-maps.js')],
      bundleIf: {
        type: 'node',
        test: ({data = {}}) => data.widget && data.widget.type === 'map',
      },
    },
  ],
});
